import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchSupabasePrintableAssignment, issueSupabaseAssignmentQr } from '../../features/runtime/supabaseAdapter';
import type { AssignmentQrIssueResultV1, PrintableAssignmentManifestV1 } from '../../features/qr/types';
import { QrCodeImage } from '../../components/QrCodeImage';

function promptText(prompt: unknown) {
  if (typeof prompt === 'string') return prompt;
  if (prompt && typeof prompt === 'object') {
    const record = prompt as Record<string, unknown>;
    for (const key of ['text', 'prompt', 'display']) if (typeof record[key] === 'string') return String(record[key]);
  }
  return 'Soal BelajarKu';
}

export function PrintableAssignmentPage() {
  const { assignmentId = '' } = useParams();
  const [manifest, setManifest] = useState<PrintableAssignmentManifestV1 | null>(null);
  const [qr, setQr] = useState<AssignmentQrIssueResultV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setManifest(null); setError(null);
    void fetchSupabasePrintableAssignment(assignmentId)
      .then((result) => { if (!cancelled) setManifest(result); })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Assignment tidak dapat dimuat.'); });
    return () => { cancelled = true; };
  }, [assignmentId]);

  async function createQr() {
    setQrError(null);
    try { setQr(await issueSupabaseAssignmentQr(assignmentId)); }
    catch (cause) { setQrError(cause instanceof Error ? cause.message : 'QR belum dapat dibuat.'); }
  }

  if (error) return <main className="print-assignment"><div className="feedback feedback--error"><strong>Tampilan cetak belum tersedia.</strong><p>{error}</p></div></main>;
  if (!manifest) return <main className="print-assignment"><p>Menyiapkan worksheet frozen…</p></main>;

  return (
    <main className="print-assignment">
      <header className="print-assignment__header">
        <div>
          <div className="eyebrow">BelajarKu · {manifest.subject.name}</div>
          <h1>Worksheet {manifest.assignment.type}</h1>
          <p>{manifest.student.displayName} · {manifest.student.studentCode}</p>
        </div>
        <div className="print-assignment__meta">
          <span>Tanggal: {manifest.assignment.assignedDate}</span>
          <span>ID: {manifest.assignment.id.slice(0, 8)}</span>
        </div>
      </header>

      <section className="no-print print-controls card">
        <strong>Worksheet ini berasal dari question instances yang sudah frozen.</strong>
        <p className="muted">Cetak tidak membuat soal baru. Soal yang dilihat siswa, tutor, dan history tetap identik.</p>
        <div className="button-row">
          <button className="btn btn--primary" type="button" onClick={() => window.print()}>Cetak / Simpan PDF</button>
          <button className="btn" type="button" onClick={createQr}>Buat Private Assignment QR</button>
          <Link className="btn" to="/student/today">Kembali</Link>
        </div>
        {qrError && <p className="feedback feedback--error">{qrError}</p>}
      </section>

      {qr && (
        <aside className="print-qr-box">
          <strong>Private Assignment QR</strong>
          <QrCodeImage path={qr.path} alt={`QR assignment ${manifest.assignment.id}`} />
          <small>Berlaku sampai {new Date(qr.expiresAt).toLocaleDateString('id-ID')}.</small>
          <small className="no-print">QR menunjuk ke private assignment resolver dan tetap memerlukan autentikasi/otorisasi.</small>
        </aside>
      )}

      <ol className="print-question-list">
        {manifest.questions.map((question, index) => (
          <li key={question.questionInstanceId} className="print-question">
            <div className="print-question__prompt">{promptText(question.prompt)}</div>
            <div className="print-answer-space" aria-hidden="true" />
            <small className="print-skill-code">{question.skillCode}</small>
          </li>
        ))}
      </ol>

      <footer className="print-assignment__footer">BelajarKu · Frozen Assignment · Tidak memuat kunci jawaban</footer>
    </main>
  );
}
